import { useSelector } from 'react-redux';

import { getLookupData } from 'modules/api/lookups';
import withCancelRequest from 'components/hocs/withCancelRequest';
import AsyncSelect from 'lib/AsyncSelect';
import { DealStatus } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import { getIsReseller } from 'store/publisher/selectors';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { AsyncSelectLoadDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { CodeName, LocationProps } from './Location.types';

const handleLocationSelect =
  <T extends CodeName>(onChange: (items: T[]) => void): SelectHandler<T> =>
  (_, value) => {
    onChange(value as T[]);
  };

const Location = ({
  cancelFunctions,
  countries = [],
  onChangeCountries,
  streets = [],
  onChangeStreets,
  cities = [],
  onChangeCities,
  postCodes = [],
  onChangePostCodes,
  counties = [],
  onChangeCounties,
}: LocationProps): React.ReactElement => {
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const readOnly = useSelector(getDealFormReadOnly) && !isPendingReservation;
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const isReseller = useSelector(getIsReseller);

  const onLoadLookupData: AsyncSelectLoadDataHandler<CodeName> = async (dropdownName, query) => {
    if (query.length < 2) return [];

    try {
      const requestArguments = { cancelFunctions, lookupURLName: dropdownName, query };
      if (isReseller) {
        const response = (await multiPublisherRequest(environmentId, resellerMediaOwners, getLookupData, {
          ...requestArguments,
        })) as (Pick<CodeNameModel, 'code' | 'name'> & { publisher: string })[];
        return response.map(({ publisher, code, name }) => ({
          code,
          mediaOwner: publisher,
          name: `${publisher} : ${name}`,
        }));
      }
      const response = (await getLookupData(requestArguments)) as CodeName[];
      return response.map((item) => ({ ...item, mediaOwner: environmentId }));
    } catch {
      return [];
    }
  };

  return (
    <div className="grid grid-cols-2 gap-x-7 xl:gap-x-24 gap-y-11 px-12 py-6" data-test-id="location">
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60" data-test-id="country-header">
          Country
        </p>
        <AsyncSelect
          dataTestId="country"
          name="country"
          loadData={onLoadLookupData}
          selectedValue={countries}
          placeholder="Select country"
          onSelect={handleLocationSelect(onChangeCountries)}
          multiple
          isDisabled={readOnly}
        />
      </div>
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60" data-test-id="street-header">
          Street
        </p>
        <AsyncSelect
          dataTestId="street"
          name="address"
          loadData={onLoadLookupData}
          selectedValue={streets}
          placeholder="Select street"
          onSelect={handleLocationSelect(onChangeStreets)}
          multiple
          isDisabled={readOnly}
        />
      </div>
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60" data-test-id="city-header">
          City
        </p>
        <AsyncSelect
          dataTestId="city"
          name="town"
          loadData={onLoadLookupData}
          selectedValue={cities}
          placeholder="Select city"
          onSelect={handleLocationSelect(onChangeCities)}
          multiple
          isDisabled={readOnly}
        />
      </div>
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60" data-test-id="county-header">
          County/State/Province
        </p>
        <AsyncSelect
          dataTestId="county"
          name="county"
          loadData={onLoadLookupData}
          selectedValue={counties}
          placeholder="Select address"
          onSelect={handleLocationSelect(onChangeCounties)}
          multiple
          isDisabled={readOnly}
        />
      </div>
      <div>
        <p className="mb-2 sub-header-base text-neutral-950-opacity-60" data-test-id="postcode-header">
          Postcode
        </p>
        <AsyncSelect
          dataTestId="postcode"
          name="postcode"
          loadData={onLoadLookupData}
          selectedValue={postCodes}
          placeholder="Select postcode/zipcode"
          onSelect={handleLocationSelect(onChangePostCodes)}
          multiple
          isDisabled={readOnly}
        />
      </div>
    </div>
  );
};

export default withCancelRequest(Location);
